/**
 * Exception Criteria Presets
 * 
 * Provides pre-built exception criteria templates that users can select
 * and customize. Each preset generates a rule set that can be applied
 * to weather data evaluation.
 */

/**
 * Available colors for exception criteria
 * These map to Bootstrap classes or custom hex colors
 */
export const CRITERION_COLORS = [
  { id: 'warning', label: 'Yellow', class: 'bg-warning text-dark', hex: '#ffc107' },
  { id: 'danger', label: 'Red', class: 'bg-danger', hex: '#dc3545' },
  { id: 'info', label: 'Blue', class: 'bg-info', hex: '#0dcaf0' },
  { id: 'success', label: 'Green', class: 'bg-success', hex: '#198754' },
  { id: 'purple', label: 'Purple', class: 'bg-purple', hex: '#6f42c1' },
  { id: 'orange', label: 'Orange', class: 'bg-orange', hex: '#fd7e14' },
  { id: 'pink', label: 'Pink', class: 'bg-pink', hex: '#d63384' },
  { id: 'teal', label: 'Teal', class: 'bg-teal', hex: '#20c997' },
  { id: 'indigo', label: 'Indigo', class: 'bg-indigo', hex: '#6610f2' },
  { id: 'cyan', label: 'Cyan', class: 'bg-cyan', hex: '#0891b2' },
  { id: 'lime', label: 'Lime', class: 'bg-lime', hex: '#84cc16' }
];

/**
 * Default color assignments for weather fields.
 * Chosen to be intuitive and visually distinct when multiple criteria appear together.
 */
export const DEFAULT_FIELD_COLORS = {
  precipprob: 'info',       // Blue - rain/precipitation probability
  precip: 'info',           // Blue - precipitation amount
  tempmax: 'danger',        // Red - extreme heat
  tempmin: 'teal',          // Teal - cold
  temp: 'orange',           // Orange - general temperature
  feelslikemax: 'danger',   // Red - feels-like heat
  feelslikemin: 'teal',     // Teal - feels-like cold
  feelslike: 'orange',      // Orange - feels-like general
  dew: 'cyan',              // Cyan - moisture/humidity
  humidity: 'cyan',         // Cyan - humidity
  windspeed: 'purple',      // Purple - wind
  windgust: 'purple',       // Purple - wind gusts
  winddir: 'purple',        // Purple - wind direction
  pressure: 'indigo',       // Indigo - barometric pressure
  visibility: 'indigo',     // Indigo - visibility/fog
  cloudcover: 'indigo',     // Indigo - cloud cover
  uvindex: 'pink',          // Pink - UV radiation
  solarradiation: 'warning',// Yellow - solar
  solarenergy: 'warning',   // Yellow - solar energy
  snow: 'cyan',             // Cyan - snow (icy)
  snowdepth: 'cyan',        // Cyan - snow depth
  severerisk: 'danger',     // Red - severe risk
  conditions: 'warning',    // Yellow - general conditions
  alerts: 'danger',         // Red - weather alerts/warnings
  moonphase: 'indigo'       // Indigo - astronomical
};

/**
 * Get the default color for a weather field.
 * @param {string} field - Field ID from WEATHER_FIELDS
 * @returns {string} Color ID from CRITERION_COLORS
 */
export function getDefaultColorForField(field) {
  return DEFAULT_FIELD_COLORS[field] || 'warning';
}

export const OPERATORS = {
  GT: { id: 'gt', label: '>', description: 'Greater than' },
  GTE: { id: 'gte', label: '≥', description: 'Greater than or equal' },
  LT: { id: 'lt', label: '<', description: 'Less than' },
  LTE: { id: 'lte', label: '≤', description: 'Less than or equal' },
  BETWEEN: { id: 'between', label: 'between', description: 'Between (inclusive)' },
  CONTAINS: { id: 'contains', label: 'contains', description: 'Text contains' },
  PRESENT: { id: 'present', label: 'is present', description: 'Value exists/is true' },
  NOT_PRESENT: { id: 'notpresent', label: 'is not present', description: 'Value is absent/false' },
  DISPLAY: { id: 'display', label: '(display only)', description: 'Show value in grid without triggering exceptions' }
};

export const FIELD_TYPES = {
  NUMERIC: 'numeric',
  TEXT: 'text',
  BOOLEAN: 'boolean'
};

/**
 * Weather fields available for exception criteria
 * Field metadata includes units for both metric and us systems
 * section: 'days' = daily only, 'hours' = hourly only, 'both' = user can choose
 * Only tempmax and tempmin are daily-only (not available in hourly data)
 */
export const WEATHER_FIELDS = {
  tempmax: {
    id: 'tempmax',
    label: 'Max Temperature',
    type: FIELD_TYPES.NUMERIC,
    section: 'days',
    units: { us: '°F', metric: '°C' }
  },
  tempmin: {
    id: 'tempmin',
    label: 'Min Temperature',
    type: FIELD_TYPES.NUMERIC,
    section: 'days',
    units: { us: '°F', metric: '°C' }
  },
  temp: {
    id: 'temp',
    label: 'Temperature',
    type: FIELD_TYPES.NUMERIC,
    section: 'both',
    units: { us: '°F', metric: '°C' }
  },
  precip: {
    id: 'precip',
    label: 'Precipitation',
    type: FIELD_TYPES.NUMERIC,
    section: 'both',
    units: { us: 'in', metric: 'mm' }
  },
  precipprob: {
    id: 'precipprob',
    label: 'Precipitation Probability',
    type: FIELD_TYPES.NUMERIC,
    section: 'both',
    units: { us: '%', metric: '%' }
  },
  windspeed: {
    id: 'windspeed',
    label: 'Wind Speed',
    type: FIELD_TYPES.NUMERIC,
    section: 'both',
    units: { us: 'mph', metric: 'km/h' }
  },
  windgust: {
    id: 'windgust',
    label: 'Wind Gust',
    type: FIELD_TYPES.NUMERIC,
    section: 'both',
    units: { us: 'mph', metric: 'km/h' }
  },
  snow: {
    id: 'snow',
    label: 'Snow',
    type: FIELD_TYPES.NUMERIC,
    section: 'both',
    units: { us: 'in', metric: 'cm' }
  },
  snowdepth: {
    id: 'snowdepth',
    label: 'Snow Depth',
    type: FIELD_TYPES.NUMERIC,
    section: 'days',
    units: { us: 'in', metric: 'cm' }
  },
  conditions: {
    id: 'conditions',
    label: 'Conditions',
    type: FIELD_TYPES.TEXT,
    section: 'both',
    units: { us: '', metric: '' }
  },
  alerts: {
    id: 'alerts',
    label: 'Weather Alerts',
    type: FIELD_TYPES.BOOLEAN,
    section: 'days',
    units: { us: '', metric: '' }
  },
  humidity: {
    id: 'humidity',
    label: 'Humidity',
    type: FIELD_TYPES.NUMERIC,
    section: 'both',
    units: { us: '%', metric: '%' }
  },
  severerisk: {
    id: 'severerisk',
    label: 'Severe Risk',
    type: FIELD_TYPES.NUMERIC,
    section: 'days',
    units: { us: '%', metric: '%' }
  }
};

/**
 * Criteria options for the dropdown selector.
 * Each entry combines a weather field with a granularity level (daily / hourly).
 * For 'both' fields, two entries are generated so the user can pick explicitly.
 * Daily-only fields (tempmax, tempmin, snowdepth, severerisk, alerts) get a single 'daily' entry.
 * The `key` is used as the v-model value; it encodes field + granularity.
 */
export const CRITERIA_OPTIONS = (() => {
  const opts = [];
  const order = [
    'tempmax', 'tempmin', 'temp',
    'precip', 'precipprob', 'snow', 'snowdepth',
    'windspeed', 'windgust',
    'humidity', 'conditions', 'severerisk', 'alerts'
  ];
  const groups = {
    tempmax: 'Temperature', tempmin: 'Temperature', temp: 'Temperature',
    precip: 'Precipitation', precipprob: 'Precipitation', snow: 'Precipitation', snowdepth: 'Precipitation',
    windspeed: 'Wind', windgust: 'Wind',
    humidity: 'Other', conditions: 'Other', severerisk: 'Other', alerts: 'Other'
  };
  for (const fieldId of order) {
    const f = WEATHER_FIELDS[fieldId];
    if (!f) continue;
    const group = groups[fieldId] || 'Other';
    if (f.section === 'days') {
      // Daily-only — single entry, no prefix needed (unambiguous)
      opts.push({ key: `${fieldId}:daily`, field: fieldId, granularity: 'daily', label: f.label, group });
    } else if (f.section === 'both') {
      // Offer both daily and hourly
      opts.push({ key: `${fieldId}:daily`,  field: fieldId, granularity: 'daily',  label: `Daily ${f.label}`,  group });
      opts.push({ key: `${fieldId}:hourly`, field: fieldId, granularity: 'hourly', label: `Hourly ${f.label}`, group });
    } else {
      // hours-only (none currently, but future-proof)
      opts.push({ key: `${fieldId}:hourly`, field: fieldId, granularity: 'hourly', label: `Hourly ${f.label}`, group });
    }
  }
  return opts;
})();

/**
 * Unit conversion factors and functions
 * Converts values between US and Metric unit systems
 */
const UNIT_CONVERSIONS = {
  // Temperature: °F <-> °C
  temperature: {
    usToMetric: (f) => (f - 32) * 5/9,
    metricToUs: (c) => c * 9/5 + 32
  },
  // Precipitation: inches <-> mm
  precipitation: {
    usToMetric: (inches) => inches * 25.4,
    metricToUs: (mm) => mm / 25.4
  },
  // Speed: mph <-> km/h
  speed: {
    usToMetric: (mph) => mph * 1.60934,
    metricToUs: (kmh) => kmh / 1.60934
  },
  // Snow: inches <-> cm
  snow: {
    usToMetric: (inches) => inches * 2.54,
    metricToUs: (cm) => cm / 2.54
  }
};

/**
 * Maps field IDs to their conversion type
 */
const FIELD_CONVERSION_TYPE = {
  tempmax: 'temperature',
  tempmin: 'temperature',
  temp: 'temperature',
  precip: 'precipitation',
  windspeed: 'speed',
  windgust: 'speed',
  snow: 'snow',
  snowdepth: 'snow'
  // Fields without conversions: precipprob, conditions, alerts
};

/**
 * Conversion types that should use whole numbers (no decimals)
 */
const WHOLE_NUMBER_CONVERSIONS = ['temperature', 'speed'];

/**
 * Converts a single value from one unit system to another
 * @param {string} fieldId - Field ID from WEATHER_FIELDS
 * @param {number} value - Value to convert
 * @param {string} fromUnit - Source unit system ('us' or 'metric')
 * @param {string} toUnit - Target unit system ('us' or 'metric')
 * @returns {number} Converted value (whole number for temp/wind, 1 decimal for others)
 */
export function convertValue(fieldId, value, fromUnit, toUnit) {
  if (fromUnit === toUnit) return value;
  if (value === null || value === undefined || isNaN(value)) return value;

  const conversionType = FIELD_CONVERSION_TYPE[fieldId];
  if (!conversionType) return value; // No conversion needed

  const converters = UNIT_CONVERSIONS[conversionType];
  if (!converters) return value;

  const convertFn = fromUnit === 'us' ? converters.usToMetric : converters.metricToUs;
  const converted = convertFn(value);
  
  // Round to whole number for temperature and wind speed, 1 decimal for others
  if (WHOLE_NUMBER_CONVERSIONS.includes(conversionType)) {
    return Math.round(converted);
  }
  return Math.round(converted * 10) / 10;
}

/**
 * Converts all criteria values in a ruleset from one unit system to another
 * @param {Object} ruleset - Ruleset to convert
 * @param {string} fromUnit - Source unit system ('us' or 'metric')
 * @param {string} toUnit - Target unit system ('us' or 'metric')
 * @returns {Object} Ruleset with converted values (mutates in place and returns)
 */
export function convertRulesetUnits(ruleset, fromUnit, toUnit) {
  if (!ruleset || !ruleset.criteria || fromUnit === toUnit) return ruleset;

  for (const criterion of ruleset.criteria) {
    const conversionType = FIELD_CONVERSION_TYPE[criterion.field];
    if (!conversionType) continue; // Skip fields that don't need conversion

    if (Array.isArray(criterion.value)) {
      // 'between' operator uses array [min, max]
      criterion.value = criterion.value.map(v => convertValue(criterion.field, v, fromUnit, toUnit));
    } else if (typeof criterion.value === 'number') {
      criterion.value = convertValue(criterion.field, criterion.value, fromUnit, toUnit);
    }
  }

  return ruleset;
}

/**
 * Converts all rulesets' criteria values from one unit system to another
 * @param {Array} rulesets - Array of rulesets
 * @param {string} fromUnit - Source unit system ('us' or 'metric')
 * @param {string} toUnit - Target unit system ('us' or 'metric')
 * @returns {Array} Array of rulesets with converted values
 */
export function convertAllRulesetsUnits(rulesets, fromUnit, toUnit) {
  if (!Array.isArray(rulesets) || fromUnit === toUnit) return rulesets;
  
  for (const ruleset of rulesets) {
    convertRulesetUnits(ruleset, fromUnit, toUnit);
  }
  
  return rulesets;
}

/**
 * Creates a new criterion object
 * @param {string} field - Field ID from WEATHER_FIELDS
 * @param {string} operator - Operator ID from OPERATORS
 * @param {*} value - Single value or array for 'between'
 * @param {string} granularity - 'daily' or 'hourly' (legacy 'days'/'hours'/'both' accepted for compat)
 * @param {string} name - Optional custom name for the criterion
 * @returns {Object} Criterion object
 */
export function createCriterion(field, operator, value, granularity = 'daily', name = null, options = {}) {
  // Normalise legacy section values to new granularity values
  if (granularity === 'days' || granularity === 'both') granularity = 'daily';
  if (granularity === 'hours') granularity = 'hourly';

  return {
    id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).substr(2),
    name, // Custom name (shown in UI if set)
    field,
    operator,
    value,
    granularity, // 'daily' or 'hourly'
    enabled: true,
    color: options.color || getDefaultColorForField(field), // Color for exception highlighting
    isStub: options.isStub || false // Stub criteria only show values in grid, don't trigger exceptions
  };
}

/**
 * Creates a new ruleset containing multiple criteria
 * @param {string} name - Display name for the ruleset
 * @param {Array} criteria - Array of criterion objects
 * @param {string} combineMode - 'AND' or 'OR'
 * @returns {Object} Ruleset object
 */
export function createRuleset(name, criteria = [], combineMode = 'OR') {
  return {
    id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).substr(2),
    name,
    criteria,
    combineMode, // 'AND' or 'OR'
    createdAt: Date.now()
  };
}

/**
 * Pre-built exception criteria presets
 * Each preset is a factory function that returns a ruleset
 */
export const PRESETS = {
  temperatureAbove: {
    id: 'temperatureAbove',
    name: 'Temperature above X',
    description: 'Alert when max temperature exceeds a threshold',
    icon: 'bi-thermometer-high',
    category: 'temperature',
    factory: (value = 35, unitGroup = 'metric') => createRuleset(
      `Temp above ${value}°`,
      [createCriterion('tempmax', 'gt', unitGroup === 'us' ? 95 : value, 'days', 'Temperature above X')],
      'OR'
    )
  },

  tooHotForShipping: {
    id: 'tooHotForShipping',
    name: 'Too hot for shipping',
    description: 'Temperature conditions that may affect package integrity (typically >30°C/86°F)',
    icon: 'bi-box-seam',
    category: 'logistics',
    factory: (unitGroup = 'metric') => createRuleset(
      'Too hot for shipping',
      [createCriterion('tempmax', 'gt', unitGroup === 'us' ? 86 : 30, 'days', 'Too hot for shipping')],
      'OR'
    )
  },

  tooColdForConcrete: {
    id: 'tooColdForConcrete',
    name: 'Too cold for concrete pour',
    description: 'Conditions unsafe for concrete work (typically <5°C/40°F)',
    icon: 'bi-building',
    category: 'construction',
    factory: (unitGroup = 'metric') => createRuleset(
      'Too cold for concrete',
      [createCriterion('tempmin', 'lt', unitGroup === 'us' ? 40 : 5, 'days', 'Too cold for concrete')],
      'OR'
    )
  },

  rainExpected: {
    id: 'rainExpected',
    name: 'Rain expected',
    description: 'Significant probability of precipitation (>50%)',
    icon: 'bi-cloud-rain',
    category: 'precipitation',
    factory: (threshold = 50) => createRuleset(
      'Rain expected',
      [createCriterion('precipprob', 'gte', threshold, 'days', 'Rain expected')],
      'OR'
    )
  },

  weatherWarning: {
    id: 'weatherWarning',
    name: 'Weather warning active',
    description: 'Any active weather alert for the location',
    icon: 'bi-exclamation-triangle',
    category: 'alerts',
    factory: () => createRuleset(
      'Weather warning',
      [createCriterion('alerts', 'present', true, 'days', 'Weather warning')],
      'OR'
    )
  },

  highWind: {
    id: 'highWind',
    name: 'High winds',
    description: 'Wind speed or gusts exceeding safe thresholds',
    icon: 'bi-wind',
    category: 'wind',
    factory: (unitGroup = 'metric') => createRuleset(
      'High winds',
      [
        createCriterion('windspeed', 'gt', unitGroup === 'us' ? 30 : 50, 'days', 'High wind speed'),
        createCriterion('windgust', 'gt', unitGroup === 'us' ? 45 : 75, 'days', 'High wind gust')
      ],
      'OR'
    )
  },

  snowExpected: {
    id: 'snowExpected',
    name: 'Snow expected',
    description: 'Any snow accumulation forecasted',
    icon: 'bi-snow',
    category: 'precipitation',
    factory: () => createRuleset(
      'Snow expected',
      [createCriterion('snow', 'gt', 0, 'days', 'Snow expected')],
      'OR'
    )
  },

  severeConditions: {
    id: 'severeConditions',
    name: 'Severe weather conditions',
    description: 'Storm, thunder, or severe weather in conditions text',
    icon: 'bi-cloud-lightning',
    category: 'severe',
    factory: () => createRuleset(
      'Severe conditions',
      [
        createCriterion('conditions', 'contains', 'storm', 'days', 'Storm'),
        createCriterion('conditions', 'contains', 'thunder', 'days', 'Thunder'),
        createCriterion('conditions', 'contains', 'severe', 'days', 'Severe')
      ],
      'OR'
    )
  }
};

/**
 * Checks if a given datetime falls within any alert's onset/ends time range
 * @param {Array} alerts - Array of alert objects from the API
 * @param {string} datetime - The datetime to check (ISO format date or datetime)
 * @param {string} timezone - Optional timezone from the location data
 * @returns {Object} { hasActiveAlert: boolean, matchingAlerts: Array }
 */
function checkAlertsForDatetime(alerts, datetime, timezone = null) {
  if (!Array.isArray(alerts) || alerts.length === 0 || !datetime) {
    return { hasActiveAlert: false, matchingAlerts: [] };
  }

  const matchingAlerts = [];
  
  // Parse the datetime we're checking
  // Day format: "2024-01-15" - represents the full day
  // Hour format: "00:00:00" or full datetime
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(datetime);
  
  for (const alert of alerts) {
    if (!alert.onset && !alert.ends) {
      // Alert has no time bounds - assume it's active for all queried dates
      matchingAlerts.push(alert);
      continue;
    }

    try {
      // Parse alert onset and ends times
      const alertOnset = alert.onset ? new Date(alert.onset) : null;
      const alertEnds = alert.ends ? new Date(alert.ends) : null;

      if (isDateOnly) {
        // For day-level check: alert overlaps if the alert's time range 
        // intersects with any part of the day (00:00:00 to 23:59:59)
        const dayStart = new Date(datetime + 'T00:00:00');
        const dayEnd = new Date(datetime + 'T23:59:59');

        // Check overlap: alert starts before day ends AND alert ends after day starts
        const onsetBeforeDayEnds = !alertOnset || alertOnset <= dayEnd;
        const endsAfterDayStarts = !alertEnds || alertEnds >= dayStart;

        if (onsetBeforeDayEnds && endsAfterDayStarts) {
          matchingAlerts.push(alert);
        }
      } else {
        // For hour-level check: parse as full datetime or combine with day
        let checkTime;
        if (/^\d{2}:\d{2}:\d{2}$/.test(datetime)) {
          // Hour only format - we'd need the day context, skip for now
          // This case is handled in evaluateRulesetForDay with dayData.datetime
          continue;
        } else {
          checkTime = new Date(datetime);
        }

        // Check if this specific time falls within the alert window
        const afterOnset = !alertOnset || checkTime >= alertOnset;
        const beforeEnds = !alertEnds || checkTime <= alertEnds;

        if (afterOnset && beforeEnds) {
          matchingAlerts.push(alert);
        }
      }
    } catch (e) {
      // If date parsing fails, be conservative and include the alert
      matchingAlerts.push(alert);
    }
  }

  return {
    hasActiveAlert: matchingAlerts.length > 0,
    matchingAlerts
  };
}

/**
 * Checks if a specific hour on a given day falls within any alert's time range
 * @param {Array} alerts - Array of alert objects from the API
 * @param {string} dayDatetime - The day's datetime (e.g., "2024-01-15")
 * @param {string} hourDatetime - The hour's datetime (e.g., "00:00:00")
 * @returns {Object} { hasActiveAlert: boolean, matchingAlerts: Array }
 */
function checkAlertsForHour(alerts, dayDatetime, hourDatetime) {
  if (!Array.isArray(alerts) || alerts.length === 0 || !dayDatetime || !hourDatetime) {
    return { hasActiveAlert: false, matchingAlerts: [] };
  }

  // Combine day and hour into full datetime
  const fullDatetime = `${dayDatetime}T${hourDatetime}`;
  return checkAlertsForDatetime(alerts, fullDatetime);
}

/**
 * Evaluates a single criterion against a weather data point
 * @param {Object} criterion - The criterion to evaluate
 * @param {Object} data - Weather data object (day or hour)
 * @param {Object} context - Optional context for special fields like alerts
 * @param {Array} context.alerts - Location-level alerts array
 * @param {string} context.datetime - The datetime being evaluated
 * @param {string} context.dayDatetime - For hours, the parent day's datetime
 * @returns {boolean} Whether the criterion is satisfied
 */
export function evaluateCriterion(criterion, data, context = null) {
  if (!criterion || !criterion.enabled || !data) return false;

  const fieldValue = data[criterion.field];
  const operator = criterion.operator;
  const testValue = criterion.value;

  // Handle alerts specially - check if alerts are active for this specific datetime
  if (criterion.field === 'alerts') {
    const alerts = context?.alerts || data.alerts;
    if (!Array.isArray(alerts) || alerts.length === 0) {
      return operator === 'notpresent';
    }

    // Use the datetime context to check if alerts are active for this specific time
    const datetime = context?.datetime || data.datetime;
    const dayDatetime = context?.dayDatetime;
    
    let alertCheck;
    if (dayDatetime && datetime) {
      // Hour-level check
      alertCheck = checkAlertsForHour(alerts, dayDatetime, datetime);
    } else if (datetime) {
      // Day-level check
      alertCheck = checkAlertsForDatetime(alerts, datetime);
    } else {
      // Fallback: just check if any alerts exist
      alertCheck = { hasActiveAlert: alerts.length > 0, matchingAlerts: alerts };
    }

    return operator === 'present' ? alertCheck.hasActiveAlert : !alertCheck.hasActiveAlert;
  }

  // Handle null/undefined values
  if (fieldValue === null || fieldValue === undefined) {
    return operator === 'notpresent';
  }

  // Text operators
  if (WEATHER_FIELDS[criterion.field]?.type === FIELD_TYPES.TEXT) {
    const strValue = String(fieldValue).toLowerCase();
    if (operator === 'contains') {
      return strValue.includes(String(testValue).toLowerCase());
    }
    return false;
  }

  // Boolean operators
  if (operator === 'present') {
    return !!fieldValue;
  }
  if (operator === 'notpresent') {
    return !fieldValue;
  }

  // Numeric operators
  const numValue = parseFloat(fieldValue);
  if (isNaN(numValue)) return false;

  switch (operator) {
    case 'gt':
      return numValue > testValue;
    case 'gte':
      return numValue >= testValue;
    case 'lt':
      return numValue < testValue;
    case 'lte':
      return numValue <= testValue;
    case 'between':
      if (Array.isArray(testValue) && testValue.length === 2) {
        return numValue >= testValue[0] && numValue <= testValue[1];
      }
      return false;
    default:
      return false;
  }
}

/**
 * Check if an hour's time falls within business hours window.
 * @param {string} hourDatetime - Hour datetime in "HH:mm:ss" format
 * @param {Object} businessHours - { enabled, start: 'HH:mm', end: 'HH:mm' }
 * @returns {boolean} true if within business hours or businessHours not enabled
 */
function isWithinBusinessHours(hourDatetime, businessHours) {
  if (!businessHours || !businessHours.enabled) return true;
  // hourDatetime is "HH:mm:ss" from the hours array
  const hhmm = hourDatetime.substring(0, 5); // "HH:mm"
  return hhmm >= businessHours.start && hhmm < businessHours.end;
}

/**
 * Evaluates a ruleset against weather data for a single day.
 * Each criterion carries a `granularity` property ('daily' or 'hourly').
 *   - 'daily' criteria evaluate against the day-level aggregate fields only.
 *   - 'hourly' criteria evaluate against individual hour records. If any single
 *     hour (optionally filtered by businessHours) matches, the day is flagged.
 * @param {Object} ruleset - The ruleset to evaluate
 * @param {Object} dayData - Day data from Timeline API (includes .hours[])
 * @param {boolean} includeHours - Whether hourly data was fetched & available
 * @param {Object} locationData - Optional location-level data (for alerts)
 * @param {Object} businessHours - Optional { enabled, start, end } — only applies to hourly criteria
 * @returns {Object} Evaluation result with matched criteria info
 */
export function evaluateRulesetForDay(ruleset, dayData, includeHours = false, locationData = null, businessHours = null) {
  if (!ruleset || !ruleset.criteria || !dayData) {
    return { matched: false, matchedCriteria: [], matchedHours: [], alertMatchedHours: [], hourCriteriaMap: {} };
  }

  const matchedCriteria = [];
  const matchedHours = [];
  const alertMatchedHours = [];
  const hourCriteriaMap = {};
  const isAndMode = ruleset.combineMode === 'AND';

  const alertContext = {
    alerts: locationData?.alerts || [],
    datetime: dayData.datetime
  };

  for (const criterion of ruleset.criteria) {
    if (!criterion.enabled || criterion.isStub || criterion.operator === 'display') continue;

    // Normalise legacy section values
    const gran = (criterion.granularity === 'hourly' || criterion.section === 'hours')
      ? 'hourly'
      : 'daily';

    let criterionMatched = false;

    // ── Alerts ──────────────────────────────────────────────
    if (criterion.field === 'alerts') {
      // Day-level alert check (always)
      if (evaluateCriterion(criterion, dayData, alertContext)) {
        criterionMatched = true;
      }
      // Identify which specific hours have alerts (for hourly display)
      if (includeHours && dayData.hours && alertContext.alerts.length > 0) {
        for (const hour of dayData.hours) {
          const hourContext = {
            alerts: alertContext.alerts,
            datetime: hour.datetime,
            dayDatetime: dayData.datetime
          };
          if (evaluateCriterion(criterion, hour, hourContext)) {
            if (!alertMatchedHours.includes(hour.datetime)) alertMatchedHours.push(hour.datetime);
            if (!matchedHours.includes(hour.datetime)) matchedHours.push(hour.datetime);
            if (!hourCriteriaMap[hour.datetime]) hourCriteriaMap[hour.datetime] = [];
            if (!hourCriteriaMap[hour.datetime].includes(criterion.id)) hourCriteriaMap[hour.datetime].push(criterion.id);
            criterionMatched = true;
          }
        }
      }
    }
    // ── Daily criteria ─────────────────────────────────────
    else if (gran === 'daily') {
      if (evaluateCriterion(criterion, dayData)) {
        criterionMatched = true;
      }
      // Mark hours for display: if the day-level criterion matched, mark ALL hours
      // (since the risk applies to the whole day). If it didn't match, mark nothing.
      if (criterionMatched && includeHours && dayData.hours) {
        for (const hour of dayData.hours) {
          if (!matchedHours.includes(hour.datetime)) matchedHours.push(hour.datetime);
          if (!hourCriteriaMap[hour.datetime]) hourCriteriaMap[hour.datetime] = [];
          if (!hourCriteriaMap[hour.datetime].includes(criterion.id)) hourCriteriaMap[hour.datetime].push(criterion.id);
        }
      }
    }
    // ── Hourly criteria ────────────────────────────────────
    else if (gran === 'hourly' && includeHours && dayData.hours) {
      for (const hour of dayData.hours) {
        // Apply business hours filter for hourly criteria only
        if (!isWithinBusinessHours(hour.datetime, businessHours)) continue;

        if (evaluateCriterion(criterion, hour)) {
          criterionMatched = true;
          if (!matchedHours.includes(hour.datetime)) matchedHours.push(hour.datetime);
          if (!hourCriteriaMap[hour.datetime]) hourCriteriaMap[hour.datetime] = [];
          if (!hourCriteriaMap[hour.datetime].includes(criterion.id)) hourCriteriaMap[hour.datetime].push(criterion.id);
        }
      }
    }

    if (criterionMatched) {
      matchedCriteria.push(criterion.id);
    }

    // Short-circuit for AND mode
    if (isAndMode && !criterionMatched) {
      return { matched: false, matchedCriteria: [], matchedHours: [], alertMatchedHours: [], hourCriteriaMap: {} };
    }
  }

  const matched = matchedCriteria.length > 0;
  return { matched, matchedCriteria, matchedHours, alertMatchedHours, hourCriteriaMap };
}

/**
 * Evaluates a ruleset against all days for a location
 * @param {Object} ruleset - The ruleset to evaluate
 * @param {Object} locationData - Location response from Timeline API
 * @param {boolean} includeHours - Whether to evaluate hourly criteria
 * @param {Object} businessHours - Optional { enabled, start, end } for hourly filtering
 * @returns {Object} Evaluation result with all matching days
 */
export function evaluateRulesetForLocation(ruleset, locationData, includeHours = false, businessHours = null) {
  if (!locationData || !locationData.days) {
    return { hasExceptions: false, exceptionDays: [], exceptionCount: 0 };
  }

  const exceptionDays = [];

  for (const day of locationData.days) {
    const result = evaluateRulesetForDay(ruleset, day, includeHours, locationData, businessHours);
    if (result.matched) {
      exceptionDays.push({
        date: day.datetime,
        day,
        matchedCriteria: result.matchedCriteria,
        matchedHours: result.matchedHours,
        alertMatchedHours: result.alertMatchedHours,
        hourCriteriaMap: result.hourCriteriaMap
      });
    }
  }

  return {
    hasExceptions: exceptionDays.length > 0,
    exceptionDays,
    exceptionCount: exceptionDays.length,
    nextExceptionDate: exceptionDays.length > 0 ? exceptionDays[0].date : null
  };
}

/**
 * Checks if a specific day has any active alerts based on onset/ends times
 * Useful for displaying alert indicators in the UI
 * @param {Array} alerts - Location-level alerts array
 * @param {string} dayDatetime - Day's datetime (e.g., "2024-01-15")
 * @returns {Object} { hasActiveAlert: boolean, matchingAlerts: Array }
 */
export function getActiveAlertsForDay(alerts, dayDatetime) {
  if (!Array.isArray(alerts) || alerts.length === 0 || !dayDatetime) {
    return { hasActiveAlert: false, matchingAlerts: [] };
  }

  const matchingAlerts = [];
  
  for (const alert of alerts) {
    if (!alert.onset && !alert.ends) {
      // Alert has no time bounds - assume it's active for all queried dates
      matchingAlerts.push(alert);
      continue;
    }

    try {
      const alertOnset = alert.onset ? new Date(alert.onset) : null;
      const alertEnds = alert.ends ? new Date(alert.ends) : null;

      // Check if alert overlaps with this day
      const dayStart = new Date(dayDatetime + 'T00:00:00');
      const dayEnd = new Date(dayDatetime + 'T23:59:59');

      const onsetBeforeDayEnds = !alertOnset || alertOnset <= dayEnd;
      const endsAfterDayStarts = !alertEnds || alertEnds >= dayStart;

      if (onsetBeforeDayEnds && endsAfterDayStarts) {
        matchingAlerts.push(alert);
      }
    } catch (e) {
      // If date parsing fails, be conservative and include the alert
      matchingAlerts.push(alert);
    }
  }

  return {
    hasActiveAlert: matchingAlerts.length > 0,
    matchingAlerts
  };
}

/**
 * Gets available operators for a given field type
 * @param {string} fieldId - Field ID from WEATHER_FIELDS
 * @returns {Array} Array of available operator objects
 */
export function getOperatorsForField(fieldId) {
  const field = WEATHER_FIELDS[fieldId];
  if (!field) return [OPERATORS.DISPLAY];

  let operators;
  switch (field.type) {
    case FIELD_TYPES.NUMERIC:
      operators = [OPERATORS.GT, OPERATORS.GTE, OPERATORS.LT, OPERATORS.LTE, OPERATORS.BETWEEN];
      break;
    case FIELD_TYPES.TEXT:
      operators = [OPERATORS.CONTAINS];
      break;
    case FIELD_TYPES.BOOLEAN:
      operators = [OPERATORS.PRESENT, OPERATORS.NOT_PRESENT];
      break;
    default:
      operators = [];
  }
  // Always add display-only option at the end
  return [...operators, OPERATORS.DISPLAY];
}

/**
 * Deep clones a ruleset for editing
 * @param {Object} ruleset - Ruleset to clone
 * @returns {Object} Cloned ruleset with new IDs
 */
export function cloneRuleset(ruleset) {
  const cloned = JSON.parse(JSON.stringify(ruleset));
  cloned.id = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).substr(2);
  cloned.criteria.forEach(c => {
    c.id = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).substr(2);
  });
  return cloned;
}

/**
 * Format a criterion for display when it has no custom name.
 * Returns a human-readable string like "Hourly Precip Prob ≥ 50%".
 * @param {Object} c - Criterion object
 * @param {string} [unitGroup='us'] - Unit group for unit suffixes ('us', 'metric', etc.)
 * @returns {string} Formatted label
 */
export function formatCriterionLabel(c, unitGroup = 'us') {
  const fieldInfo = WEATHER_FIELDS[c.field];
  const baseLabel = fieldInfo?.label || c.field;
  const gran = c.granularity || 'daily';
  const prefix = (fieldInfo?.section === 'both') ? (gran === 'hourly' ? 'Hourly ' : 'Daily ') : '';
  const label = prefix + baseLabel;
  const ops = { gt: '>', gte: '≥', lt: '<', lte: '≤', between: '↔', contains: '~', present: '✓' };
  const op = ops[c.operator] || c.operator;
  if (c.operator === 'present') return label;
  if (c.operator === 'between' && Array.isArray(c.value)) {
    return `${label} ${c.value[0]}\u2013${c.value[1]}`;
  }
  const unit = fieldInfo?.units?.[unitGroup] || '';
  return `${label} ${op} ${c.value}${unit}`;
}
